import React, { useEffect, useRef, useState } from 'react';
import { PromoFormBlock } from '@design-system/promo-form-block';
import { getUtmParameters } from '@promo-platform/utils';

import cleanPan from './images/clean-pan.webp';
import dirtyPan from './images/dirty-pan.webp';
import handlePan from './images/handle-pan.webp';
import finishProducts from './images/img-finish-products.webp';
import tabletIcon from './images/tablet.webp';
import tabletGlow from './images/tablet-shine.webp';

const SwipeCleanScene: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => {
      setIsMobile(window.innerWidth < 768);
    };

    check();
    window.addEventListener('resize', check);

    return () => window.removeEventListener('resize', check);
  }, []);

  const CLEANING_WIDTH = isMobile ? 260 : 320;
  const SVG_SIZE = isMobile ? 260 : 320;

  const CENTER_X = SVG_SIZE / 2;

  const START_Y = SVG_SIZE * 0.08;
  const END_Y = SVG_SIZE * 0.92;

  const swipeDistance = END_Y - START_Y;

  const CLEAN_START_PROGRESS = 0.4;

  const CLEANING_SPEED = isMobile ? 0.83 : 0.98;
  const CLEANING_OFFSET = 0;

  const DIAMOND_HALF_DIAGONAL = CLEANING_WIDTH / Math.sqrt(2);

  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [showReveal, setShowReveal] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [discountCode, setDiscountCode] = useState('');

  const progressRef = useRef(0);
  const dragStartY = useRef(0);
  const dragStartProgress = useRef(0);

  const handlePointerDown = (e) => {
    if (progressRef.current >= 1) return;

    setDragging(true);

    dragStartY.current = e.clientY;
    dragStartProgress.current = progressRef.current;

    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!dragging) return;

    const deltaY = e.clientY - dragStartY.current;
    const deltaProgress = deltaY / swipeDistance;

    const calculated = dragStartProgress.current + deltaProgress;

    const nextProgress = Math.max(progressRef.current, Math.min(calculated, 1));

    if (Math.abs(nextProgress - progressRef.current) < 0.002) return;

    progressRef.current = nextProgress;
    setProgress(nextProgress);
  };

  const revealContent = () => {
    setShowReveal(true);

    const faq = document.getElementById('faq-coupon-page');

    if (faq) {
      faq.classList.add('faq-revealed-state');
    }
  };

  const handlePointerUp = () => {
    setDragging(false);

    if (progressRef.current >= 0.99) {
      progressRef.current = 1;
      setProgress(1);

      setTimeout(() => {
        revealContent();
      }, 1000);
    }
  };

  type CouponCode = {
    code: string;
  };

  const handlePromoSuccess = (_redirectUrl: string, _reset: () => void, coupon?: CouponCode) => {
    if (coupon?.code) {
      setDiscountCode(coupon.code);
    }
  };

  const hasClickedSubmit = useRef(false);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;

    if (showReveal && !hasClickedSubmit.current) {
      interval = setInterval(() => {
        const submitButton = document.querySelector(
          '.cds-promo-form button[type="submit"]'
        ) as HTMLButtonElement | null;

        if (!submitButton || submitButton.disabled) {
          return;
        }

        hasClickedSubmit.current = true;
        clearInterval(interval);

        submitButton.click();
      }, 50);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [showReveal]);

  const copyDiscountCode = async () => {
    try {
      await navigator.clipboard.writeText(discountCode);
      setIsCopied(true);
    } catch {
      // Copy failed
    }
  };

  /* DERIVED VALUES */
  const currentBrushY = START_Y + swipeDistance * progress;

  const hasStarted = progress > 0;

  const cleanProgress =
    progress <= CLEAN_START_PROGRESS
      ? 0
      : (progress - CLEAN_START_PROGRESS) / (1 - CLEAN_START_PROGRESS);

  const cleanStartY = START_Y + swipeDistance * CLEAN_START_PROGRESS;

  const cleanMaskY = cleanStartY + (currentBrushY - cleanStartY) * CLEANING_SPEED;

  const finalMaskY = Math.max(cleanStartY, cleanMaskY - CLEANING_OFFSET);

  const cleaningDiamondCenterY = finalMaskY - DIAMOND_HALF_DIAGONAL + 94;

  const showGlow = progress >= 0.4 && progress <= 0.92;

  const isCompleted = progress >= 1;

  const title = isCompleted ? 'Gratulálunk!' : 'Kevesebb súrolás, több megtakarítás';

  const subtitle = isCompleted
    ? 'Másold ki és használd fel az exkluzív Finish kedvezménykódot!'
    : 'HÚZD EL A KEDVEZMÉNY FELFEDÉSÉHEZ';

  const addInfoText = null;

  const skipButtonText = 'Kihagyás és ugrás a kuponkódhoz';

  const promoId = '51cbf92d-f91a-4c93-b25a-b49e01b6c040';
  const promoUrl =
    'https://phx-promoplatform-api-pl-prod.promoplatform.rbcloud.io/api/v1/external/forms/';

  return (
    <div className="custom-pan-animation-area">
      {!showReveal ? (
        <div className="custom-pan-animation-wrapper">
          <div className="cds-container cds-container--max-grid">
            <div className="top-title">
              <h1>
                <span>{title}</span>
              </h1>
            </div>

            <div className="extra-title desktop-hidden">
              <h2>{subtitle}</h2>
            </div>

            <div className="pan-plus-info-area">
              <div className="info-txt-block">
                <div className="extra-title">
                  <h2>{subtitle}</h2>
                </div>
                <div className="add-info-block">
                  <p>{addInfoText}</p>
                </div>
                <button type="button" className="skip-animation-btn" onClick={revealContent}>
                  {skipButtonText}
                </button>
              </div>
              <div
                className="pan-wrapper"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
              >
                <svg width={SVG_SIZE} height={SVG_SIZE} viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}>
                  <defs>
                    <mask id="clean-mask">
                      <rect width="100%" height="100%" fill="black" />

                      {cleanProgress > 0 && (
                        <rect
                          x={CENTER_X - CLEANING_WIDTH / 2}
                          y={cleaningDiamondCenterY - CLEANING_WIDTH / 2}
                          width={CLEANING_WIDTH}
                          height={CLEANING_WIDTH}
                          fill="white"
                          ry="28"
                          transform={`rotate(
                            45
                            ${CENTER_X}
                            ${cleaningDiamondCenterY}
                          )`}
                        />
                      )}
                    </mask>
                  </defs>

                  {/* Dirty pan */}
                  <image href={dirtyPan} x="0" y="0" width={SVG_SIZE} height={SVG_SIZE} />

                  {/* Clean pan */}
                  <image
                    href={cleanPan}
                    x="0"
                    y="0"
                    width={SVG_SIZE}
                    height={SVG_SIZE}
                    mask="url(#clean-mask)"
                  />
                </svg>

                {/* Tablet */}
                <div
                  className={`tablet-wrapper ${hasStarted ? 'visible' : ''}`}
                  style={{
                    left: CENTER_X,
                    transform: `translate(-50%, ${currentBrushY}px)`,
                  }}
                >
                  <img
                    src={tabletGlow}
                    alt=""
                    className={`tablet-glow ${showGlow ? 'visible' : ''}`}
                  />

                  <img src={tabletIcon} alt="" className="tablet-icon" />
                </div>

                {progress === 0 && <div className="finger-hint" />}

                <div className="pan-handle">
                  <img src={handlePan} alt="" />
                </div>
              </div>
            </div>

            <div className="progress">
              <div className="progress-label">{Math.round(progress * 100)}%</div>

              <div className="progress-track">
                <div
                  className="progress-fill"
                  style={{
                    transform: `scaleX(${progress})`,
                  }}
                />
              </div>
            </div>

            <div className="pan-area-mobile-block">
              <button type="button" className="skip-animation-btn" onClick={revealContent}>
                {skipButtonText}
              </button>

              <div className="add-info-block">
                <p>{addInfoText}</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="revealed-block">
          <div className="revealed-block-wrapper">
            <div className="revealed-block-holder">
              <div className="block-container">
                <div className="top-offer-area">
                  <div className="cds-container cds-container--max-grid">
                    <h1>
                      <span>SZEREZZ 35% KEDVEZMÉNYT</span> <br /> A MEGADOTT FINISH TERMÉKCSOMAGOKRA
                      A PEPITA OLDALÁN!
                    </h1>
                    <p>Egy exkluzív ajánlattal szeretnénk megkönnyíteni a mindennapi mosogatást.</p>
                    <div className="img-holder">
                      <img src={finishProducts} alt="Finish" />
                    </div>
                  </div>
                </div>
                <div className="discount-code-area">
                  <div className="cds-container cds-container--max-grid">
                    <h2>
                      Gratulálunk! Másold ki és használd fel az exkluzív Finish kedvezménykódot!
                    </h2>
                    <div className="discount-code-form">
                      <div className="discount-code-txt-holder">
                        <span className={`discount-code ${discountCode ? 'appeared' : ''}`}>
                          {discountCode}
                        </span>
                        <span className="bottom-txt">kuponkód</span>
                      </div>
                      <button
                        type="button"
                        className={`copy-btn ${isCopied ? 'copied' : ''}`}
                        onClick={copyDiscountCode}
                        disabled={isCopied}
                      >
                        {isCopied ? 'Kimásolva' : 'Másolás'}
                      </button>
                    </div>
                    <PromoFormBlock
                      formId={promoId}
                      url={promoUrl}
                      submitErrorMessage="Something went wrong"
                      onSuccessCallback={handlePromoSuccess}
                      utmParameters={getUtmParameters()}
                    />
                    <a
                      href="https://pepita.hu/finish-kupon-promo-cp18#ref"
                      target="_blank"
                      className="retailer-link"
                      rel="noreferrer"
                    >
                      BEVÁLTOM!
                    </a>
                  </div>
                </div>
              </div>
              <div className="img-container">
                <img src={finishProducts} width={633} height={392} alt="Finish" />
              </div>
            </div>
            <div className="add-info-block">
              <p>{addInfoText}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SwipeCleanScene;
